import axios from 'axios';
import moment from 'moment';
import type { GridSortModel, GridFilterModel } from '@mui/x-data-grid-pro';
import getAuthSession from './auth';
import { toIsoDateTime } from '../utils/adminDashboardUtils';
import { AssessmentAdminPanelRow } from '../interfaces/assessmentAdminPanel.interface';

export type GetAssessmentAdminPanelRowsArgs = {
  page: number;
  pageSize: number;
  statuses?: string[];
  sortModel?: GridSortModel;
  filterModel?: GridFilterModel;
};

export type AssessmentAdminPanelRowsResponse = {
  results: AssessmentAdminPanelRow[];
  count: number;
};

function mapGridToApiField(field: string) {
  const FIELD_MAP: Record<string, string> = {
    vendorName: 'company_name',
    siteOrApplicationName: 'site_app_name',
    assessorCompanyName: 'assessor_company_name',
    assessorName: 'assessor_name',
    assessorLevel: 'assessor_level',
    dateAccepted: 'date_accepted',
    submittedDate: 'submitted_date',
  };

  return FIELD_MAP[field] ?? field;
}

// GET /assessment-admin-panel/ — replaces getAssessmentSurveyAssessmentsForDataGrid for
// AssessmentAdminPanel: one paginated call returns the fully-annotated row (company/assessor
// names, assessment + workflow status) instead of a fetchAssessment/fetchWorkflows call per row.
export async function getAssessmentAdminPanelRows({
  page,
  pageSize,
  statuses = ['Submitted'],
  sortModel,
  filterModel,
}: GetAssessmentAdminPanelRowsArgs): Promise<AssessmentAdminPanelRowsResponse> {
  const authSession = await getAuthSession();

  const params: Record<string, any> = {
    limit: pageSize,
    offset: page * pageSize,
  };

  if (statuses?.length) {
    params.status = statuses;
  }

  const sort = sortModel?.[0];
  if (sort?.field && sort?.sort) {
    const apiField = mapGridToApiField(sort.field);
    params.ordering = sort.sort === 'asc' ? apiField : `-${apiField}`;
  }

  if (filterModel?.items?.length) {
    filterModel.items.forEach((item) => {
      if (item.value === undefined || item.value === null || String(item.value).trim() === '') return;
      if (!item.columnField) return;

      const apiField = mapGridToApiField(item.columnField);

      let value = item.value;

      if (apiField === 'date_accepted' || apiField === 'submitted_date') {
        const iso = toIsoDateTime(value);
        if (!iso) return;
        value = moment(iso).format('YYYY-MM-DD');
      }

      params[apiField] = value;
    });
  }

  const response = await axios.get(
    `${process.env.REACT_APP_BASE_API}/assessment-admin-panel/`,
    {
      headers: {
        Authorization: `Bearer ${authSession.getIdToken().getJwtToken()}`,
      },
      params,
      paramsSerializer: (p) => {
        const sp = new URLSearchParams();
        Object.entries(p).forEach(([k, v]) => {
          if (Array.isArray(v)) v.forEach((x) => sp.append(k, String(x)));
          else sp.append(k, String(v));
        });
        return sp.toString();
      },
    },
  );

  return {
    results: response.data.results as AssessmentAdminPanelRow[],
    count: response.data.count as number,
  };
}
